import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandProvider, useBrand } from './context/BrandContext';
import { NotificationProvider } from './context/NotificationContext';
import { AgentProvider } from './context/AgentContext';
import { TooltipProvider } from './components/ui/tooltip';
import { setApiActiveBrand } from './services/api';
import Login from './components/Login';
import NoRolePage from './components/NoRolePage';
import NoBrandPage from './components/NoBrandPage';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import Dashboard from './components/Dashboard';
import { VipDashboardModule } from './components/vip/index';
import SettingsView from './components/vip/SettingsView';
import NotificationsPage from './components/notifications/NotificationsPage';
import NotificationDetailPage from './components/notifications/NotificationDetailPage';
import ChatEvaluationSearch from './components/chatbot/ChatEvaluationSearch';
import ChatReview from './components/chatbot/ChatReview';
import FeedbackList from './components/chatbot/FeedbackList';
import CasinoIssuesPage from './components/casino_issues/CasinoIssuesPage';
import LivechatMiningPage from './components/livechat_mining/LivechatMiningPage';
import FlaggedChatsPage from './components/flagged_chats/FlaggedChatsPage';
import CrmCampaignsPage from './components/crm_campaigns/CrmCampaignsPage';
import TranslationChatPage from './components/translation_chat/TranslationChatPage';
import TranslationLabPage from './components/translation_lab/TranslationLabPage';
import CrmEmailPage from './components/crm_email/CrmEmailPage';
import FigmaTokensPage from './components/figma/FigmaTokensPage';
import FigmaReviewPage from './components/figma/FigmaReviewPage';
import FigmaBannerPage from './components/figma/FigmaBannerPage';
import TranslationLabSetPage from './components/translation_lab/TranslationLabSetPage';
import UserProfilePage from './components/profile/UserProfilePage';
import FigmaTokenSettingsPage from './components/profile/FigmaTokenSettingsPage';
import CrmTokenSettingsPage from './components/profile/CrmTokenSettingsPage';
import ConfigurationTokenSettingsPage from './components/profile/ConfigurationTokenSettingsPage';
import ConfigurationPage from './components/configuration/ConfigurationPage';
import PlayerTrajectoryPage from './components/trajectory/PlayerTrajectoryPage';
import VipDashboardSection from './components/VipDashboardSection';
import { VipSourceProvider, PRIVATE_VIP_SOURCE } from './context/VipSourceContext';
import PrivateBonusesPage from './components/vip_private/PrivateBonusesPage';
import PlayerProfilePage from './components/player/PlayerProfilePage';
import PlayerSearchPage from './components/player/PlayerSearchPage';
import AnniversaryBonusesPage from './components/vip/anniversary/AnniversaryBonusesPage';
import ChristmasBonusesPage from './components/vip/christmas/ChristmasBonusesPage';
import BonusRequestsPage from './components/vip/bonus_requests/BonusRequestsPage';
import ContactTrackerPage from './components/vip/contact_tracker/ContactTrackerPage';
import GameDepartmentPage from './components/game_department/GameDepartmentPage';
import GameAnalysisPage from './components/game_analysis/GameAnalysisPage';
import GameAnalysisGamePage from './components/game_analysis/GameAnalysisGamePage';
import { AvatarProvider } from './hooks/useUserAvatar';

// Forces remount when navigating between players so *Loaded flags reset cleanly.
const KeyedPlayerProfilePage = () => {
  const { userId } = useParams();
  return <PlayerProfilePage key={userId} />;
};

// The private VIP dashboard's pages: the VIP pages on the private source (one agent's book from
// the temp_ tables), under their own permission and module.
const PrivateVipRoute = ({ children }) => (
  <ProtectedRoute permission="view:vip_private" module="vip_private">
    <AppLayout>
      <VipSourceProvider source={PRIVATE_VIP_SOURCE}>{children}</VipSourceProvider>
    </AppLayout>
  </ProtectedRoute>
);
function DefaultRedirect() {
  const { permissions, loading } = useAuth();
  const { activeBrandInfo, brands } = useBrand();
  if (loading) return null;

  // 1. No brand access (roles and permissions are per brand, so none without one)
  if (brands.length === 0) return <Navigate to="/no-brand" replace />;

  // 2. Nothing granted on the active brand
  if (permissions.length === 0) return <Navigate to="/no-role" replace />;

  // 3. Has brand — redirect to first accessible module
  const modules = activeBrandInfo?.modules;
  const unsupported = activeBrandInfo?.unsupported || [];
  const hasModule = (mod) => !modules || modules.includes(mod);
  const hasFeature = (feat) => !unsupported.includes(feat);
  if (permissions.includes('view:vip_dashboard') && hasModule('vip')) return <Navigate to="/vip-dashboard" replace />;
  if (permissions.includes('view:vip_private') && hasModule('vip_private')) return <Navigate to="/vip-private" replace />;
  if (permissions.includes('view:conversion') && hasModule('conversion')) return <Navigate to="/conversion" replace />;
  if (permissions.includes('view:bot_evaluation') && hasModule('chatbot')) return <Navigate to="/bot-evaluation" replace />;
  // Feature-gated as well as module-gated: a brand can have the livechat_mining module for
  // Flagged Chats alone, and landing them on a page their warehouse can't serve would 500 on
  // first load — see livechat_mining.crm_intelligence.
  if (permissions.includes('view:livechat_mining') && hasModule('livechat_mining') && hasFeature('livechat_mining.crm_intelligence')) {
    return <Navigate to="/livechat-mining" replace />;
  }
  if (permissions.includes('view:flagged_chats') && hasModule('livechat_mining')) {
    return <Navigate to="/flagged-chats" replace />;
  }
  if (permissions.includes('view:game_department') && hasModule('game_department')) {
    return <Navigate to="/game-department" replace />;
  }

  // 4. Notifications is accessible to all authenticated users
  return <Navigate to="/notifications" replace />;
}

// Syncs active brand to the axios layer. Must be rendered inside BrandProvider.
// Switching brand needs no /api/me reload: it already returned the permissions for every brand.
//
// X-Brand is only set AFTER brands are loaded from /api/me and activeBrand is
// validated. This prevents the initial /api/me from sending a stale localStorage
// brand that the user may no longer have access to (which would cause a 403).
function BrandSync() {
  const { activeBrand, activeBrandInfo, brands, setActiveBrand } = useBrand();
  const { permissionsByBrand } = useAuth();

  // Only push the brand header once we know which brands the user can access
  useEffect(() => {
    setApiActiveBrand(brands.length > 0 ? activeBrand : null);
  }, [activeBrand, brands]);

  // A user may open a brand without holding anything on it. Rather than strand them on /no-role,
  // move to the first brand where they do hold something.
  useEffect(() => {
    const current = activeBrandInfo?.key;
    if (!current || permissionsByBrand[current]?.length) return;
    const granted = brands.find(b => permissionsByBrand[b.key]?.length);
    if (granted) setActiveBrand(granted.key);
  }, [activeBrandInfo, brands, permissionsByBrand, setActiveBrand]);

  return null;
}

function AppProviders({ children }) {
  const [brands, setBrands] = useState([]);

  const handleMeLoaded = useCallback((data) => {
    if (data.brands) {
      setBrands(data.brands);
    }
  }, []);

  return (
    <AuthProvider onMeLoaded={handleMeLoaded}>
      <BrandProvider brands={brands}>
        <BrandSync />
        <AvatarProvider>
        <NotificationProvider>
          <AgentProvider>
          <TooltipProvider>
            {children}
          </TooltipProvider>
          </AgentProvider>
        </NotificationProvider>
        </AvatarProvider>
      </BrandProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppProviders>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/no-role" element={<NoRolePage />} />
            <Route path="/no-brand" element={<NoBrandPage />} />
            <Route
              path="/vip-dashboard"
              element={
                <ProtectedRoute permission="view:vip_dashboard" module="vip">
                  <AppLayout>
                    <VipDashboardModule />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/conversion"
              element={
                <ProtectedRoute permission="view:conversion" module="conversion">
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute permission="view:vip_dashboard" module="vip">
                  <AppLayout>
                    <SettingsView />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/figma-plugin"
              element={
                <ProtectedRoute permission="manage:figma_token" module="figma_loc">
                  <AppLayout>
                    <FigmaTokensPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/figma-review"
              element={
                <ProtectedRoute permission="view:figma_localization" module="figma_loc">
                  <AppLayout>
                    <FigmaReviewPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/figma-review/:bannerId"
              element={
                <ProtectedRoute permission="view:figma_localization" module="figma_loc">
                  <AppLayout>
                    <FigmaBannerPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute permission={null}>
                  <AppLayout>
                    <NotificationsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications/:id"
              element={
                <ProtectedRoute permission={null}>
                  <AppLayout>
                    <NotificationDetailPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bot-evaluation"
              element={
                <ProtectedRoute permission="view:bot_evaluation" module="chatbot">
                  <AppLayout>
                    <ChatEvaluationSearch />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bot-evaluation/feedback"
              element={
                <ProtectedRoute permission="view:bot_feedback_list" module="chatbot">
                  <AppLayout>
                    <FeedbackList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bot-evaluation/:threadId"
              element={
                <ProtectedRoute permission="view:bot_evaluation" module="chatbot">
                  <AppLayout>
                    <ChatReview />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/casino-issues"
              element={
                <ProtectedRoute permission="manage:casino_issues" module="casino_issues">
                  <AppLayout>
                    <CasinoIssuesPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/livechat-mining"
              element={
                <ProtectedRoute permission="view:livechat_mining" module="livechat_mining" feature="livechat_mining.crm_intelligence">
                  <AppLayout>
                    <LivechatMiningPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/flagged-chats"
              element={
                <ProtectedRoute permission="view:flagged_chats" module="livechat_mining">
                  <AppLayout>
                    <FlaggedChatsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm-campaigns"
              element={
                <ProtectedRoute permission="manage:crm_campaigns" module="crm_campaigns">
                  <AppLayout>
                    <CrmCampaignsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/translation-playground"
              element={
                <ProtectedRoute permission="view:translation_chatbot" module="translation_chat">
                  <AppLayout>
                    <TranslationChatPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/translation-lab"
              element={
                <ProtectedRoute permission="view:translation_localization" module="translation_lab">
                  <AppLayout>
                    <TranslationLabPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/translation-lab/:setId"
              element={
                <ProtectedRoute permission="view:translation_localization" module="translation_lab">
                  <AppLayout>
                    <TranslationLabSetPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm-email"
              element={
                <ProtectedRoute permission="view:crm_email" module="crm_email">
                  <AppLayout>
                    <CrmEmailPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/player-trajectory"
              element={
                <ProtectedRoute permission="view:vip_dashboard" module="vip">
                  <AppLayout>
                    <PlayerTrajectoryPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/anniversary-bonuses"
              element={
                <ProtectedRoute permission="view:vip_dashboard" module="vip" feature="vip.anniversary_bonuses">
                  <AppLayout>
                    <AnniversaryBonusesPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/christmas-bonuses"
              element={
                <ProtectedRoute permission="view:vip_dashboard" module="vip" feature="vip.christmas_bonuses">
                  <AppLayout>
                    <ChristmasBonusesPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bonus-requests"
              element={
                <ProtectedRoute permission="view:vip_dashboard" module="vip" feature="vip.bonus_requests">
                  <AppLayout>
                    <BonusRequestsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contact-tracker"
              element={
                <ProtectedRoute permission="view:vip_dashboard" module="vip" feature="vip.contact_tracker">
                  <AppLayout>
                    <ContactTrackerPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/player-search"
              element={
                <ProtectedRoute permission="view:vip_dashboard" module="vip">
                  <AppLayout>
                    <PlayerSearchPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/player/:userId"
              element={
                <ProtectedRoute permission="view:vip_dashboard" module="vip">
                  <AppLayout>
                    <KeyedPlayerProfilePage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/vip-private" element={<PrivateVipRoute><VipDashboardSection /></PrivateVipRoute>} />
            <Route path="/vip-private/player-search" element={<PrivateVipRoute><PlayerSearchPage /></PrivateVipRoute>} />
            <Route path="/vip-private/bonuses" element={<PrivateVipRoute><PrivateBonusesPage /></PrivateVipRoute>} />
            <Route path="/vip-private/player/:userId" element={<PrivateVipRoute><KeyedPlayerProfilePage /></PrivateVipRoute>} />
            <Route
              path="/game-department"
              element={
                <ProtectedRoute permission="view:game_department" module="game_department">
                  <AppLayout>
                    <GameDepartmentPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/game-department/stickiness"
              element={
                <ProtectedRoute permission="view:game_department" module="game_department">
                  <AppLayout>
                    <GameDepartmentPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/game-department/metrics"
              element={
                <ProtectedRoute permission="view:game_department" module="game_department">
                  <AppLayout>
                    <GameDepartmentPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/game-department/performance"
              element={
                <ProtectedRoute permission="view:game_department" module="game_department">
                  <AppLayout>
                    <GameDepartmentPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/game-department/releases"
              element={
                <ProtectedRoute permission="view:game_department" module="game_department">
                  <AppLayout>
                    <GameDepartmentPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/game-department/pre-release-benchmark"
              element={
                <ProtectedRoute permission="view:game_department" module="game_department">
                  <AppLayout>
                    <GameDepartmentPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/game-department/ask-ai"
              element={
                <ProtectedRoute permission="view:game_department" module="game_department">
                  <AppLayout>
                    <GameDepartmentPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/game-analysis"
              element={
                <ProtectedRoute permission="view:game_department" module="game_analysis">
                  <AppLayout>
                    <GameAnalysisPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/game-analysis/:slug"
              element={
                <ProtectedRoute permission="view:game_department" module="game_analysis">
                  <AppLayout>
                    <GameAnalysisGamePage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute permission={null}>
                  <AppLayout>
                    <UserProfilePage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/figma-token"
              element={
                <ProtectedRoute permission="manage:figma_token" module="figma_loc">
                  <AppLayout>
                    <FigmaTokenSettingsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/crm-token"
              element={
                <ProtectedRoute permission="manage:crm_token" module="crm_email">
                  <AppLayout>
                    <CrmTokenSettingsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/configuration-token"
              element={
                <ProtectedRoute permission="manage:configuration">
                  <AppLayout>
                    <ConfigurationTokenSettingsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuration"
              element={
                <ProtectedRoute permission="manage:configuration">
                  <AppLayout>
                    <ConfigurationPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<DefaultRedirect />} />
          </Routes>
        </Router>
      </AppProviders>
    </Provider>
  );
}

export default App;
